/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { AccessForSelectFragment, AdditionalSignersDocument } from 'generated/resources/graphql'

import { AccessSelector } from 'modules/core/components/AccessSelector'
import LabeledInput from 'modules/core/components/LabeledInput'
import Select from 'modules/core/components/Select'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useFormProvider } from 'modules/processes/components/ItemWizard/Providers/form'
import {
  AdditionalSignerStrategy,
  addtionalSignersSelectionTypes,
} from 'modules/processes/lib/additionalSignersTypeOptions'

import { useTaskWizard } from '../../../../../Tasks/TaskProvider'

const AdditionalSigner = () => {
  const { setTaskConfiguration } = useTaskWizard()
  const { originalItem, form } = useFormProvider()
  const currentTaskConfiguration = form.get('task_type_configuration')?.value
  const [additionalSigners, setAdditionalSigners] = useState<number[]>(
    currentTaskConfiguration?.additional_signer_ids || []
  )
  const [selectionType, setSelectionType] = useState<AdditionalSignerStrategy>('manual_selection')

  const setTaskConfigurationSigners = (ids: number[] = []) => {
    setTaskConfiguration({
      additional_signer_ids: ids,
      additional_signers_strategy: selectionType,
    })
  }

  useEffect(() => {
    if (currentTaskConfiguration?.additional_signer_ids) {
      setAdditionalSigners(currentTaskConfiguration?.additional_signer_ids)
    } else if (originalItem) {
      const itemConfiguration = JSON.parse(String(originalItem.taskTypeConfiguration))
      if (itemConfiguration?.additional_signers_strategy === 'manager') {
        setSelectionType('manager')
      } else {
        setSelectionType('manual_selection')
      }
      if (itemConfiguration?.additional_signer_ids !== undefined) {
        setAdditionalSigners(itemConfiguration?.additional_signer_ids)
      }
    }
  }, [])

  useEffect(() => {
    if (!currentTaskConfiguration?.additional_signer_ids) {
      setTaskConfiguration({
        additional_signer_ids: additionalSigners,
      })
    }
  }, [currentTaskConfiguration])

  useEffect(() => {
    setTaskConfigurationSigners(additionalSigners)
  }, [additionalSigners])

  const isAdditionalSignerStrategy = (value: string): value is AdditionalSignerStrategy =>
    value === 'manager' || value === 'manual_selection'

  return (
    <Box
      background="grey000White"
      borderRadius={{ all: 'abs008' }}
      gap="s8"
      padding="s16"
      border={{ all: { width: 's1', color: 'grey400', style: 'solid' } }}
    >
      <Text size="300" weight="semibold" color="grey1000">
        {i18n.t('split_documents.assign_documents.select_additional_signees')}
      </Text>

      <Text.Normal>
        {i18n.t(
          'processes.process_settings.tasks.form.super_tasks_module.send_document.additional_signers.description'
        )}
      </Text.Normal>
      <Box gap="s16">
        <LabeledInput isLegend label={i18n.t('processes.process_settings.wizard.assignee.label')}>
          <Box gap="s6">
            <Select
              mandatory
              attachmentPosition="middle right"
              readonly={false}
              value={selectionType}
              onChange={(value) => {
                if (isAdditionalSignerStrategy(value)) {
                  setSelectionType(value)
                }
                setAdditionalSigners([])
              }}
              options={addtionalSignersSelectionTypes()}
            />
          </Box>
        </LabeledInput>
        {selectionType === 'manual_selection' && (
          <Box paddingTop="s6">
            <AdditionalSigners
              additionalSigners={additionalSigners}
              setAdditionalSigners={setAdditionalSigners}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

const AdditionalSigners = ({
  additionalSigners,
  setAdditionalSigners,
}: {
  additionalSigners: number[]
  setAdditionalSigners: (ids: number[]) => void
}) => {
  const { data } = useQuery(AdditionalSignersDocument, {
    variables: { ids: additionalSigners },
    skip: !additionalSigners.length,
  })

  const [selectedSigners, setSelectedSigners] = useState<AccessForSelectFragment[]>(
    data?.apiCore.accessesConnection.nodes || []
  )

  useEffect(() => {
    if (data?.apiCore.accessesConnection.nodes) {
      setSelectedSigners(data?.apiCore.accessesConnection.nodes)
    }
  }, [data])

  return (
    <AccessSelector
      width="s400"
      value={selectedSigners}
      multiple
      onSelect={(values) => {
        setSelectedSigners(values)
        setAdditionalSigners(values.map((value) => value.id))
      }}
      emptyLabel={i18n.t(
        'processes.process_settings.tasks.form.super_tasks_module.send_document.additional_signers.placeholder'
      )}
      terminated={false}
    />
  )
}

export default AdditionalSigner
